
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BookOpen, Award } from 'lucide-react';

interface Certification {
  id: string;
  certificate_name: string;
  issuing_organization: string;
  certification_type: string;
  issue_date: string;
  expiry_date: string | null;
  description: string | null;
  file_url: string;
  status: string;
}

interface PractitionerEducationProps {
  education?: string;
  certifications?: Certification[];
}

const PractitionerEducation: React.FC<PractitionerEducationProps> = ({ 
  education, 
  certifications = [] 
}) => {
  const { t } = useTranslation('practitioners');

  if (!education && (!certifications || certifications.length === 0)) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <BookOpen className="h-5 w-5 mr-2 text-amber-600" />
          {t('educationAndCertifications')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {education && (
          <div>
            <h4 className="font-medium text-lg mb-2 flex items-center">
              <BookOpen className="h-4 w-4 mr-1" />
              {t('education')}
            </h4>
            <p className="text-earth-700">{education}</p>
          </div>
        )}
        
        {certifications && certifications.length > 0 && (
          <div>
            <h4 className="font-medium text-lg mb-3 flex items-center">
              <Award className="h-4 w-4 mr-1" />
              Certifications
            </h4>
            <div className="space-y-3">
              {certifications.map((cert) => (
                <div key={cert.id} className="border rounded-lg p-3 bg-gray-50">
                  <div className="flex items-start justify-between mb-2">
                    <h5 className="font-medium text-earth-900">{cert.certificate_name}</h5>
                    <Badge variant="secondary" className="bg-green-100 text-green-800">
                      Verified
                    </Badge>
                  </div>
                  <p className="text-sm text-earth-600 mb-1">
                    {cert.issuing_organization}
                  </p>
                  <p className="text-xs text-earth-500">
                    Issued: {new Date(cert.issue_date).toLocaleDateString()}
                    {cert.expiry_date && (
                      <> • Expires: {new Date(cert.expiry_date).toLocaleDateString()}</>
                    )}
                  </p>
                  {cert.description && (
                    <p className="text-sm text-earth-700 mt-2">{cert.description}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PractitionerEducation;
